import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '../../shared/atoms/Box';
import Heading from '../../shared/atoms/Heading';
import EmptyState from '../../shared/molecules/EmptyState';
import WorkshopBookingCard from './WorkshopBookingCard';
import { isClosedWorkshopBooking } from '../../../utils/workshopBookingStatus';

/**
 * FIXERLIGHT-052 lot 1 — deux sections « À venir » / « Passés », dans
 * l'ORDRE EXACT rendu par l'api (contrat § 2 : « le paquet à venir reste
 * croissant, le paquet passés s'inverse » — déjà fait côté api). On ne
 * fait ici qu'un `filter`, jamais un `sort` : filter préserve l'ordre
 * relatif de chaque paquet, un sort le romprait.
 *
 * Est « à venir » un rendez-vous ni terminé ni annulé dont `scheduled_at >=
 * maintenant` (FIXERLIGHT-148 : un rendez-vous clos est passé quelle que
 * soit sa date prévue) — la même règle que l'api utilise pour construire
 * l'ordre déjà reçu. Un léger écart d'horloge entre le rendu de la liste par
 * l'api et le rendu de cet écran ne peut, au pire, mal classer qu'un unique
 * rendez-vous à la frontière — jamais réordonner un paquet.
 */
function isUpcoming(booking, now) {
  if (isClosedWorkshopBooking(booking.status)) return false;
  const date = new Date(booking.scheduled_at);
  return !Number.isNaN(date.getTime()) && date >= now;
}

const WorkshopBookingsList = ({ bookings }) => {
  const { t } = useTranslation();

  if (!bookings || !bookings.length) {
    return (
      <Box alignItems="center" data-testid="workshop-bookings-empty">
        <EmptyState
          width="60%"
          title={t('workshopappointments.empty.title')}
          subTitle={t('workshopappointments.empty.subtitle')}
        />
      </Box>
    );
  }

  const now = new Date();
  const upcoming = bookings.filter((b) => isUpcoming(b, now));
  const past = bookings.filter((b) => !isUpcoming(b, now));

  return (
    <Box data-testid="workshop-bookings-list">
      {!!upcoming.length && (
        <Box mb={upcoming.length ? 6 : 0}>
          <Heading level={4} mb={{ xs: 2, lg: 4 }}>
            {t('workshopappointments.section.upcoming')}
          </Heading>
          {upcoming.map((booking) => (
            <WorkshopBookingCard key={booking.id} booking={booking} />
          ))}
        </Box>
      )}
      {!!past.length && (
        <Box>
          <Heading level={4} mb={{ xs: 2, lg: 4 }}>
            {t('workshopappointments.section.past')}
          </Heading>
          {past.map((booking) => (
            <WorkshopBookingCard key={booking.id} booking={booking} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WorkshopBookingsList;
